import React, { useState, useContext } from 'react';
import { Typography, Box, Card, CardContent, LinearProgress, Chip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
//import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { AuthContext } from "../Auth/AuthContext";
import { formatCurrency } from "../utils/currencyFormatter";

const TopExpensesChart = ({ data }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { userCurrency } = useContext(AuthContext);

  if (!data || !data.topExpenses || data.topExpenses.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="textSecondary">
          No expense data available
        </Typography>
      </Box>
    );
  }

  const topExpenses = data.topExpenses;
  const maxAmount = Math.max(...topExpenses.map(expense => expense.amount));
  const RANK_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32', '#4A90E2', '#7B68EE'];
  
  const getRankIcon = (index) => {
    const icons = ['1', '2', '3', '4', '5'];
    return icons[index] || '📊';
  };

  const chartData = topExpenses.map((expense, index) => ({
    ...expense,
    rank: `#${index + 1}`,
    shortCategory: expense.category.length > 10 ? expense.category.substring(0, 10) + '...' : expense.category,
    color: RANK_COLORS[index]
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length && showTooltip) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: 2,
            p: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            minWidth: 200,
            maxWidth: 250
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {data.category}
          </Typography>
          <Typography variant="body2" color="primary" fontWeight="bold">
            Amount: {formatCurrency(data.amount, userCurrency)}
          </Typography>
          <Typography variant="body2">
            Transactions: {data.transactionCount}
          </Typography>
          <Typography variant="body2">
            Percentage: {data.percentage.toFixed(1)}%
          </Typography>
          <Typography variant="body2">
            Average: {formatCurrency(data.amount / data.transactionCount, userCurrency)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
        {/* <EmojiEventsIcon color="primary" /> */}
        Top 5 Expense Categories
      </Typography>

      <Card sx={{ 
        mb: 3, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <CardContent>
          <Typography variant="h4" fontWeight="bold">
            {formatCurrency(data.totalExpenses || topExpenses.reduce((sum, exp) => sum + exp.amount, 0), userCurrency)}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Total Expenses Analyzed
          </Typography>
        </CardContent>
      </Card>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart 
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="shortCategory"
            stroke="#666"
            fontSize={12}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
          />
          <YAxis 
            tickFormatter={formatCurrency}
            stroke="#666"
            fontSize={12}
          />
          <Tooltip 
            content={<CustomTooltip />}
            cursor={false}
            animationDuration={0}
            animationEasing="linear"
          />
          <Bar 
            dataKey="amount" 
            radius={[4, 4, 0, 0]}
            stroke="none"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={RANK_COLORS[index]}
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                  cursor: 'pointer'
                }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Box mt={3}>
        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
          Detailed Rankings
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          {topExpenses.map((expense, index) => (
            <Card 
              key={expense.category}
              variant="outlined"
              sx={{
                borderLeft: `6px solid ${RANK_COLORS[index]}`,
                position: 'relative',
                '&:hover': {
                  boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease-in-out'
                }
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="h6" component="span" sx={{ fontSize: '1.5rem' }}>
                        {getRankIcon(index)}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {expense.category}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                      <Chip
                        label={`${expense.transactionCount} transactions`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={`${expense.percentage.toFixed(1)}% of total`}
                        size="small"
                        sx={{ 
                          backgroundColor: RANK_COLORS[index], 
                          color: 'white',
                          '& .MuiChip-label': { fontWeight: 'bold' }
                        }}
                      />
                      <Chip
                        label={`Avg: ${formatCurrency(expense.amount / expense.transactionCount, userCurrency)}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Box>
                      <LinearProgress
                        variant="determinate"
                        value={(expense.amount / maxAmount) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#f0f0f0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: RANK_COLORS[index],
                            borderRadius: 4,
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  
                  <Box textAlign="right" ml={2}>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {formatCurrency(expense.amount, userCurrency)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Amount
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      <Box mt={3} p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          💡 Quick Insights
        </Typography>
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="body2">
            • <strong>{topExpenses[0]?.category}</strong> is your biggest expense at{' '}
            <strong>{topExpenses[0]?.percentage.toFixed(1)}%</strong> of total spending
          </Typography>
          <Typography variant="body2">
            • Top 3 categories account for{' '}
            <strong>{topExpenses.slice(0, 3).reduce((sum, exp) => sum + exp.percentage, 0).toFixed(1)}%</strong> of expenses
          </Typography>
          <Typography variant="body2">
            • Average transaction value: <strong>{formatCurrency(
              topExpenses.reduce((sum, exp) => sum + exp.amount, 0) / 
              topExpenses.reduce((sum, exp) => sum + exp.transactionCount, 0), 
              userCurrency
            )}</strong>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TopExpensesChart;